<template>
  <div class="hint-chips">
    <el-button
      v-for="chip in chips"
      :key="chip.prompt"
      size="small"
      class="hint-chip ml-0"
      :class="{ 'is-clicked': chip.isClicked }"
      :icon="chip.isClicked ? CheckIcon : undefined"
      :disabled="disabled || chip.isClicked"
      data-testid="ai-chat-hint-chip-button"
      @click="handleClick(chip)"
    >
      {{ chip.label }}
    </el-button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { CheckIcon } from '@shared/assets/icons'
import type { HintChip, HintChipsBlock } from '../../types'

/* follow-up chips; each chip asks its prompt once */
const props = withDefaults(
  defineProps<{
    block: HintChipsBlock
    /* true while a turn streams: chips cannot start a second one */
    disabled?: boolean
  }>(),
  { disabled: false }
)

const emit = defineEmits<{
  followup: [prompt: string]
}>()

/* prompts clicked here before the parent marks their chip `clicked` */
const clickedPrompts = ref<string[]>([])

const chips = computed(() =>
  props.block.chips.map((chip) => ({
    ...chip,
    isClicked:
      Boolean(chip.clicked) || clickedPrompts.value.includes(chip.prompt)
  }))
)

const handleClick = (chip: HintChip & { isClicked: boolean }): void => {
  if (props.disabled || chip.isClicked) return
  clickedPrompts.value.push(chip.prompt)
  emit('followup', chip.prompt)
}
</script>

<style scoped>
.hint-chips {
  display: flex;
  flex-wrap: wrap;
  align-self: flex-start;
  gap: 0.5rem;
  max-width: 88%;
}

.hint-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border: 1px solid var(--el-border-color);
  border-radius: 0.25rem;
  background: var(--el-bg-color);
  color: var(--el-text-color-primary);
  font-size: 0.6875rem;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
}

.hint-chip.is-clicked {
  border-color: var(--el-border-color-main);
  background: var(--el-fill-color);
  color: var(--el-text-color-secondary);
  cursor: default;
}

.hint-chip-check {
  width: 0.75rem;
  height: 0.75rem;
  color: var(--primary-color);
}
</style>
